import {
  createContext,
  useContext,
  useEffect,
  type PropsWithChildren
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SessionUser } from "@htzap/contracts";
import { api, jsonBody } from "./api";

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  preferencesSaving: boolean;
  login: (input: {
    email: string;
    password: string;
  }) => Promise<SessionUser>;
  changePassword: (input: {
    currentPassword: string;
    newPassword: string;
  }) => Promise<SessionUser>;
  setMessageSignatureEnabled: (enabled: boolean) => Promise<SessionUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  useEffect(() => {
    const expireSession = () => {
      queryClient.setQueryData(["auth", "me"], { user: null });
      queryClient.cancelQueries({
        predicate: (query) => !(
          query.queryKey[0] === "login-composition" &&
          query.queryKey[1] === "public"
        )
      });
    };
    window.addEventListener("htzap:session-expired", expireSession);
    return () => window.removeEventListener("htzap:session-expired", expireSession);
  }, [queryClient]);
  const me = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => api<{ user: SessionUser }>("/v1/auth/me"),
    retry: false,
    staleTime: 60_000
  });
  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<{ user: SessionUser }>("/v1/auth/login", {
        method: "POST",
        ...jsonBody(input)
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["auth", "me"], data);
    }
  });
  const logoutMutation = useMutation({
    mutationFn: () => api("/v1/auth/logout", { method: "POST" }),
    onSuccess: () => {
      queryClient.clear();
    }
  });
  const changePasswordMutation = useMutation({
    mutationFn: (input: {
      currentPassword: string;
      newPassword: string;
    }) =>
      api<{ user: SessionUser }>("/v1/auth/password", {
        method: "POST",
        ...jsonBody(input)
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["auth", "me"], data);
    }
  });
  const preferencesMutation = useMutation({
    mutationFn: (messageSignatureEnabled: boolean) =>
      api<{ user: SessionUser }>("/v1/auth/preferences", {
        method: "PATCH",
        ...jsonBody({ messageSignatureEnabled })
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["auth", "me"], data);
    }
  });

  return (
    <AuthContext.Provider
      value={{
        user: me.data?.user || null,
        loading: me.isLoading,
        preferencesSaving: preferencesMutation.isPending,
        login: async (input) => {
          const result = await loginMutation.mutateAsync(input);
          return result.user;
        },
        changePassword: async (input) => {
          const result = await changePasswordMutation.mutateAsync(input);
          return result.user;
        },
        setMessageSignatureEnabled: async (enabled) => {
          const result = await preferencesMutation.mutateAsync(enabled);
          return result.user;
        },
        logout: async () => {
          await logoutMutation.mutateAsync();
        }
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider ausente.");
  return value;
}
